import * as React from 'react';
import {cva, type VariantProps} from 'class-variance-authority';
import {cn} from '@common-web/lib/utils';

// Each themeable variant carries a stable `cb-badge--<variant>` hook class so
// individual portals can restyle badges in their own CSS (see the badge
// override block in apps/client-synrgy/src/index.css) without forking this
// component. The Tailwind classes here are the default look for every app.
const badgeVariants = cva(
  'cb-badge inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'cb-badge--default bg-blue-500 text-black/50 border border-blue-500/20',
        secondary: 'cb-badge--secondary bg-purple-500 text-white border border-purple-500/20',
        gray: 'bg-internal-brand-gray text-black border border-internal-brand-gray/20',
        destructive: 'bg-destructive text-white border border-destructive/20',
        outline: 'border border-neutral-300 text-neutral-700',
        success:
          'cb-badge--success bg-gradient-to-r from-emerald/5 to-teal/5 text-emerald border border-emerald/20',
        // teal/green exist so sub-stage maps can distinguish "Active" and
        // "Client Approved"; by default they render identically to `success` —
        // only portals that override the hook classes (synrgy) show distinct
        // flat greens.
        teal: 'cb-badge--teal bg-gradient-to-r from-emerald/5 to-teal/5 text-emerald border border-emerald/20',
        green:
          'cb-badge--green bg-gradient-to-r from-emerald/5 to-teal/5 text-emerald border border-emerald/20',
        warning:
          'cb-badge--warning bg-gradient-to-r from-amber/50 to-amber text-white border border-amber/20',
        info: 'cb-badge--info bg-gradient-to-r from-primary-blue-light/5 to-primary-blue/5 text-primary-blue border border-primary-blue/20',
        purple: 'bg-gradient-to-r from-indigo/5 to-indigo text-indigo border border-indigo/20',
        // New gradient variants
        'gradient-primary': 'bg-gradient-to-r from-primary-blue to-indigo text-white border-0',
        'gradient-success': 'bg-gradient-to-r from-emerald to-teal text-white border-0',
        'gradient-warning': 'bg-gradient-to-r from-amber to-yellow-500 text-white border-0',
        'gradient-danger': 'bg-gradient-to-r from-rose to-red-500 text-white border-0',
      },
      size: {
        default: 'px-3 py-1 text-xs',
        sm: 'px-2 py-0.5 text-[10px]',
        lg: 'px-4 py-1.5 text-sm',
      },
      // New animation prop
      animate: {
        none: '',
        pulse: 'animate-pulse-subtle',
        glow: 'shadow-sm hover:shadow-glow-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      animate: 'none',
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  icon?: React.ReactNode;
}

function Badge({className, variant, size, animate, icon, children, ...props}: BadgeProps) {
  return (
    <div className={cn(badgeVariants({variant, size, animate}), className)} {...props}>
      {icon && <span className='mr-1'>{icon}</span>}
      {children}
    </div>
  );
}

export {Badge, badgeVariants};
